import axios from "axios";
import { jwtDecode } from "jwt-decode";
import { API_BASE_URL } from "../../../../js/api.js";
import {
  normalizarPayloadAcompanhamentosMe,
} from "../utils/empresa.js";
import {
  normalizarEmpresaAcompanhamentoItem,
  deduplicarAcompanhamentosPorEmpresa,
} from "../mappers/empresaMapper.js";
import {
  obterTipoProgramaUsuarioLogado,
} from "../utils/tipoPrograma.js";
import {
  obterDocumentoUsuarioLogadoPagina,
} from "../utils/session.js";
import {
  resolverCpfAprendiz,
} from "../utils/itemResolvers.js";
import {
  criarLogger,
  extrairListaDeResposta,
} from "./acompanhamentoHelpers.js";

const logEstudante = criarLogger("Estudante");

/**
 * Extrai o CPF do estudante com múltiplas fontes de fallback:
 *   1. localStorage: "cpf_estudante", "estudante_cpf", "cpf", "documento", "documento_usuario"
 *   2. localStorage: "user" e "usuario" (JSON parseado, checando cpf, documento, etc.)
 *   3. JWT: decoded.cpf, decoded.cpf_estudante, decoded.documento, decoded.sub (se 11 dígitos)
 *   4. obterDocumentoUsuarioLogadoPagina() (se 11 dígitos)
 *
 * @param {string} [token]
 * @returns {string} CPF de 11 dígitos ou string vazia
 */
export function resolverCpfEstudanteCompleto(token) {
  if (typeof window !== "undefined") {
    const chavesStorage = [
      "cpf_estudante",
      "estudante_cpf",
      "cpf",
      "documento",
      "documento_usuario",
    ];
    for (const k of chavesStorage) {
      try {
        const v = window.localStorage.getItem(k);
        if (v) {
          const limpo = String(v).replace(/\D/g, "");
          if (limpo.length === 11) return limpo;
        }
      } catch (_) {}
    }

    for (const k of ["user", "usuario"]) {
      try {
        const raw = window.localStorage.getItem(k);
        if (raw) {
          const u = JSON.parse(raw);
          const cands = [
            u?.cpf,
            u?.cpf_estudante,
            u?.estudante_cpf,
            u?.documento,
            u?.usuario_documento,
            u?.cpf_aprendiz,
            u?.aprendiz_cpf,
          ];
          for (const cand of cands) {
            if (cand) {
              const limpo = String(cand).replace(/\D/g, "");
              if (limpo.length === 11) return limpo;
            }
          }
        }
      } catch (_) {}
    }
  }

  const t =
    token ||
    (typeof window !== "undefined" ? window.localStorage.getItem("token") : "");
  if (t) {
    try {
      const dec = jwtDecode(t);
      const cands = [
        dec?.cpf,
        dec?.cpf_estudante,
        dec?.estudante_cpf,
        dec?.documento,
        dec?.documento_usuario,
        dec?.cpf_aprendiz,
      ];
      for (const cand of cands) {
        if (cand) {
          const limpo = String(cand).replace(/\D/g, "");
          if (limpo.length === 11) return limpo;
        }
      }
      if (dec?.sub) {
        const limpo = String(dec.sub).replace(/\D/g, "");
        if (limpo.length === 11) return limpo;
      }
    } catch (_) {}
  }

  try {
    const doc = obterDocumentoUsuarioLogadoPagina();
    if (doc) {
      const limpo = String(doc).replace(/\D/g, "");
      if (limpo.length === 11) return limpo;
    }
  } catch (_) {}

  return "";
}

/**
 * Resolve o tipo_programa (0 = TCE, 1 = TCA, 2 = TCA Empregador) do estudante.
 *
 * @param {string} [token]
 * @returns {number} 0, 1 ou 2
 */
export function resolverTipoProgramaEstudante(token) {
  if (typeof window !== "undefined") {
    try {
      const rawUser =
        window.localStorage.getItem("user") ||
        window.localStorage.getItem("usuario");
      if (rawUser) {
        const u = JSON.parse(rawUser);
        const tp = Number(u?.tipo_programa);
        if (Number.isFinite(tp) && (tp === 0 || tp === 1 || tp === 2)) return tp;
      }
    } catch (_) {}
  }

  const t =
    token ||
    (typeof window !== "undefined" ? window.localStorage.getItem("token") : "");
  if (t) {
    try {
      const dec = jwtDecode(t);
      const tp = Number(dec?.tipo_programa);
      if (Number.isFinite(tp) && (tp === 0 || tp === 1 || tp === 2)) return tp;
    } catch (_) {}
  }

  return obterTipoProgramaUsuarioLogado();
}

/**
 * Consulta endpoints por CPF do estudante com múltiplos fallbacks (TCE, TCA Empregador, TCA Comum).
 *
 * @param {string} cpf
 * @param {number} tipoPrograma
 * @param {string} token
 * @returns {Promise<Array<object>>}
 */
async function buscarAutorizacoesPorCpfEndpoints(cpf, tipoPrograma, token) {
  const endpoints = [];
  if (tipoPrograma === 0) {
    endpoints.push({
      url: `${API_BASE_URL}/tce/estudante/${cpf}/autorizacoes`,
      tipo: 0,
      _origem: "tce",
    });
    endpoints.push({
      url: `${API_BASE_URL}/tca-empregador/estudante/${cpf}/autorizacoes`,
      tipo: 2,
      _origem: "tca-empregador",
    });
  } else if (tipoPrograma === 2) {
    endpoints.push({
      url: `${API_BASE_URL}/tca-empregador/estudante/${cpf}/autorizacoes`,
      tipo: 2,
      _origem: "tca-empregador",
    });
    endpoints.push({
      url: `${API_BASE_URL}/tce/estudante/${cpf}/autorizacoes`,
      tipo: 0,
      _origem: "tce",
    });
  } else {
    endpoints.push({
      url: `${API_BASE_URL}/tce/estudante/${cpf}/autorizacoes`,
      tipo: 0,
      _origem: "tce",
    });
    endpoints.push({
      url: `${API_BASE_URL}/tca-empregador/estudante/${cpf}/autorizacoes`,
      tipo: 2,
      _origem: "tca-empregador",
    });
  }
  endpoints.push({
    url: `${API_BASE_URL}/tca/estudante/${cpf}/autorizacoes`,
    tipo: 1,
    _origem: "tca",
  });

  const itens = [];
  const resultados = await Promise.allSettled(
    endpoints.map(async ({ url, tipo, _origem }) => {
      logEstudante("GET", url);
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 10000,
      });
      const lista = extrairListaDeResposta(resp?.data);
      return lista.map((it) => ({
        ...it,
        _origem: it?._origem || _origem,
        tipo_programa: it?.tipo_programa ?? tipo,
      }));
    }),
  );

  for (const r of resultados) {
    if (r.status === "fulfilled" && Array.isArray(r.value) && r.value.length > 0) {
      itens.push(...r.value);
    }
  }

  return itens;
}

/**
 * Consulta endpoints por query string (?cpf=) como fallback secundário.
 *
 * @param {string} cpf
 * @param {string} token
 * @returns {Promise<Array<object>>}
 */
async function buscarAutorizacoesPorCpfQuery(cpf, token) {
  const endpointsQuery = [
    {
      url: `${API_BASE_URL}/tce?cpf=${encodeURIComponent(cpf)}`,
      tipo: 0,
      _origem: "tce",
    },
    {
      url: `${API_BASE_URL}/tca-empregador?cpf=${encodeURIComponent(cpf)}`,
      tipo: 2,
      _origem: "tca-empregador",
    },
    {
      url: `${API_BASE_URL}/tca?cpf=${encodeURIComponent(cpf)}`,
      tipo: 1,
      _origem: "tca",
    },
  ];

  const itens = [];
  const resultados = await Promise.allSettled(
    endpointsQuery.map(async ({ url, tipo, _origem }) => {
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });
      const lista = extrairListaDeResposta(resp?.data);
      return lista.map((it) => ({
        ...it,
        _origem: it?._origem || _origem,
        tipo_programa: it?.tipo_programa ?? tipo,
      }));
    }),
  );

  for (const r of resultados) {
    if (r.status === "fulfilled" && Array.isArray(r.value) && r.value.length > 0) {
      itens.push(...r.value);
    }
  }

  return itens;
}

/**
 * Resgate por IDs persistidos na sessão (ex: estudante_tce_id, estudante_tca_empregador_id).
 *
 * @param {string} token
 * @returns {Promise<Array<object>>}
 */
async function resgatarAutorizacoesPorIdsSessao(token) {
  let tceId = null;
  let tcaEmpId = null;
  let tcaId = null;

  if (typeof window !== "undefined") {
    tceId = window.localStorage.getItem("estudante_tce_id");
    tcaEmpId = window.localStorage.getItem("estudante_tca_empregador_id");
    tcaId = window.localStorage.getItem("estudante_tca_id");
    try {
      const u = JSON.parse(window.localStorage.getItem("user") || "{}");
      if (!tceId && u?.tce_id) tceId = u.tce_id;
      if (!tcaEmpId && u?.tca_empregador_id) tcaEmpId = u.tca_empregador_id;
      if (!tcaId && u?.tca_id) tcaId = u.tca_id;
    } catch (_) {}
  }

  const resgates = [
    { id: tceId, url: `${API_BASE_URL}/tce/${tceId}`, tipo: 0, _origem: "tce" },
    {
      id: tcaEmpId,
      url: `${API_BASE_URL}/tca-empregador/${tcaEmpId}`,
      tipo: 2,
      _origem: "tca-empregador",
    },
    { id: tcaId, url: `${API_BASE_URL}/tca/${tcaId}`, tipo: 1, _origem: "tca" },
  ].filter((r) => r.id);

  const itens = [];
  const resultados = await Promise.allSettled(
    resgates.map(async ({ url, tipo, _origem }) => {
      logEstudante("RESCUE GET", url);
      const resp = await axios.get(url, {
        headers: { Authorization: `Bearer ${token}` },
        timeout: 8000,
      });
      const obj = resp?.data?.data || resp?.data;
      if (
        obj &&
        typeof obj === "object" &&
        (obj.id || obj.tce_id || obj.tca_id || obj.tca_empregador_id)
      ) {
        return {
          ...obj,
          _origem: obj?._origem || _origem,
          tipo_programa: obj?.tipo_programa ?? tipo,
        };
      }
      return null;
    }),
  );

  for (const r of resultados) {
    if (r.status === "fulfilled" && r.value) {
      itens.push(r.value);
    }
  }

  return itens;
}

/**
 * Cria fallback defensivo a partir do objeto user da sessão quando todas
 * as chamadas anteriores falharem.
 *
 * @param {string} cpf
 * @param {number} tipoPrograma
 * @returns {Array<object>}
 */
// eslint-disable-next-line no-unused-vars
function gerarFallbackDefensivoEstudante(cpf, tipoPrograma) {
  if (typeof window === "undefined") return [];

  try {
    const rawUser =
      window.localStorage.getItem("user") ||
      window.localStorage.getItem("usuario");
    if (!rawUser) return [];

    const u = JSON.parse(rawUser);
    const ehEstudanteUser =
      u?.tipo === "estudante" ||
      u?.tipo === "aprendiz" ||
      Number(u?.acesso_autorizacao ?? 0) === 1 ||
      Number(u?.tipo_acesso ?? 0) === 1;

    if (!ehEstudanteUser) return [];

    const idFallback =
      u?.id ||
      u?.estudante_id ||
      u?.tce_id ||
      u?.tca_empregador_id ||
      u?.tca_id ||
      1;
    const tpUser = Number.isFinite(Number(u?.tipo_programa))
      ? Number(u.tipo_programa)
      : (tipoPrograma ?? 0);

    return [
      {
        id: idFallback,
        tce_id: tpUser === 0 ? idFallback : null,
        tca_empregador_id: tpUser === 2 ? idFallback : null,
        tca_id: tpUser === 1 ? idFallback : null,
        empresa_id: u?.empresa_id || 1,
        empresa_nome:
          u?.empresa_nome ||
          u?.razao_social ||
          u?.nome_empresa ||
          "Empresa Vinculada",
        nome:
          u?.nome ||
          u?.estudante_nome ||
          u?.nome_aprendiz ||
          "Estagiário / Aprendiz",
        estudante_nome:
          u?.nome ||
          u?.estudante_nome ||
          u?.nome_aprendiz ||
          "Estagiário / Aprendiz",
        nome_aprendiz:
          u?.nome ||
          u?.estudante_nome ||
          u?.nome_aprendiz ||
          "Estagiário / Aprendiz",
        cpf: cpf || u?.cpf || "",
        estudante_cpf: cpf || u?.cpf || "",
        cpf_aprendiz: cpf || u?.cpf || "",
        cnpj: u?.cnpj || u?.empresa_cnpj || "",
        cnpj_empresa: u?.cnpj || u?.empresa_cnpj || "",
        tipo_programa: tpUser,
        _origem: tpUser === 0 ? "tce" : tpUser === 2 ? "tca-empregador" : "tca",
        etapa_atual: 2,
        total_etapas: tpUser === 1 ? 4 : 5,
        status: "AGUARDANDO_PREENCHIMENTO",
        status_label:
          tpUser === 0
            ? "Aguardando Preenchimento do Estagiário"
            : "Aguardando Preenchimento do Aprendiz",
        raw: u,
      },
    ];
  } catch (_) {
    return [];
  }
}

/**
 * Lista as autorizações vinculadas ao CPF do estudante logado.
 *
 * Estratégia em camadas:
 *   1. Tenta a rota agregada `/acompanhamentos/me`.
 *   2. Se vazia e temos CPF, consulta os endpoints por CPF.
 *   3. Se vazia, tenta endpoints por query string (?cpf=).
 *   4. Se vazia, resgata por IDs persistidos na sessão.
 *   5. Fallback defensivo com dados do usuário da sessão.
 *
 * @param {string} [token]
 * @returns {Promise<Array<object>>}
 */
export async function listarAutorizacoesDoEstudante(token) {
  if (typeof window === "undefined") return [];
  const t = token || window.localStorage.getItem("token");
  if (!t) return [];

  const cpf = resolverCpfEstudanteCompleto(t);
  const tipoPrograma = resolverTipoProgramaEstudante(t);

  logEstudante("CPF do estudante resolvido:", cpf, "tipo_programa:", tipoPrograma);

  let itensBrutos = [];

  // 1. Rota agregada /acompanhamentos/me
  try {
    logEstudante("GET", `${API_BASE_URL}/acompanhamentos/me`);
    const respMe = await axios.get(`${API_BASE_URL}/acompanhamentos/me`, {
      headers: { Authorization: `Bearer ${t}` },
      timeout: 10000,
    });
    const listaMe = extrairListaDeResposta(respMe?.data);
    if (listaMe.length > 0) {
      logEstudante("OK (/acompanhamentos/me) — itens:", listaMe.length);
      itensBrutos.push(...listaMe);
    }
  } catch (err) {
    logEstudante("FALHA (/acompanhamentos/me):", err?.response?.status, err?.message);
  }

  // 2. Se não trouxe itens e temos CPF, consulta endpoints por CPF
  if (itensBrutos.length === 0 && cpf) {
    const itensCpf = await buscarAutorizacoesPorCpfEndpoints(cpf, tipoPrograma, t);
    itensBrutos.push(...itensCpf);
  }

  // 3. Fallback adicional via query param (?cpf=)
  if (itensBrutos.length === 0 && cpf) {
    const itensQuery = await buscarAutorizacoesPorCpfQuery(cpf, t);
    itensBrutos.push(...itensQuery);
  }

  // 4. Rescue por IDs salvos na sessão/login
  if (itensBrutos.length === 0) {
    const itensRescue = await resgatarAutorizacoesPorIdsSessao(t);
    itensBrutos.push(...itensRescue);
  }

  // Se todas as consultas legítimas retornarem vazio, o estudante realmente não possui processos ativos

  if (itensBrutos.length === 0) {
    logEstudante("RESULTADO FINAL: lista vazia.");
    return [];
  }

  const payloadFinal = { data: itensBrutos };
  const { lista: listaBruta } = normalizarPayloadAcompanhamentosMe(payloadFinal);
  logEstudante("lista extraida do payload:", listaBruta.length);

  const cpfLimpo = String(cpf || "").replace(/\D/g, "");
  const normalizada = listaBruta
    .map((item, idx) => {
      const antes = item;
      const depois = normalizarEmpresaAcompanhamentoItem(item);
      if (!depois && antes) {
        logEstudante(
          "item descartado pelo normalizador na posicao",
          idx,
          "— chaves top-level:",
          Object.keys(antes),
        );
      }
      return depois;
    })
    .filter(Boolean)
    .filter((item) => {
      if (cpfLimpo.length !== 11) return true;
      const cpfItem = resolverCpfAprendiz(item);
      return !cpfItem || cpfItem.length !== 11 || cpfItem === cpfLimpo;
    });

  const deduplicada = deduplicarAcompanhamentosPorEmpresa(normalizada);
  logEstudante("total apos normalizacao e deduplicacao:", deduplicada.length);
  return deduplicada;
}
